/**
 * OnboardingBypassForm — Iter. 411.hh
 * ────────────────────────────────────
 * Minimal essentials form rendered when the user picks Option B on the
 * OnboardingPathChoice gate. Collects the 5 fields required for the
 * macro calculator + Coach YUMIX to work:
 *   weight · target_weight · height · age · sex · goal
 *
 * Handoff back to Onboarding.js: parent owns the bypassData state and
 * the submit handler. This component is pure presentation.
 *
 * GLOW aesthetic, violet-anchored to signal "expert path".
 */
import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Zap, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { UNIT_SYSTEMS } from '../utils/unitConversions';

// Reusable pill button — same GLOW pattern as the rest of YUMIX.
const PillOption = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="flex-1 py-2.5 px-3 rounded-lg text-[11px] font-black uppercase tracking-widest transition-all active:scale-95"
    style={{
      background: active
        ? 'linear-gradient(140deg, rgba(168,85,247,0.55) 0%, rgba(124,58,237,0.35) 100%)'
        : 'linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(15,15,15,0.55) 45%, rgba(168,85,247,0.08) 100%)',
      border: active
        ? '1px solid rgba(168,85,247,0.75)'
        : '1px solid rgba(168,85,247,0.25)',
      boxShadow: active
        ? '0 0 12px rgba(168,85,247,0.55), inset 0 1px 0 rgba(255,255,255,0.1)'
        : 'inset 0 1px 0 rgba(255,255,255,0.03)',
      color: active ? '#fff' : '#c4b5fd99',
      textShadow: active ? '0 0 5px rgba(255,255,255,0.4)' : 'none',
      backdropFilter: 'blur(4px)',
    }}
  >
    {children}
  </button>
);

export default function OnboardingBypassForm({
  language,
  t,
  unitSystem,
  setUnitSystem,
  bypassData,
  setBypassData,
  loading,
  onBack,
  onSubmit,
}) {
  const fr = language === 'fr';
  const es = language === 'es';
  const isImperial = unitSystem === 'imperial';

  const label = (fr_, en_, es_ = fr_) => (fr ? fr_ : es ? es_ : en_);

  const update = (patch) => setBypassData(prev => ({ ...prev, ...patch }));

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="w-full max-w-lg mx-auto py-4"
      data-testid="onboarding-bypass-form"
    >
      {/* Back link */}
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition-colors mb-4"
        data-testid="bypass-back-btn"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {label('Retour', 'Back', 'Volver')}
      </button>

      <div
        className="rounded-2xl p-6"
        style={{
          background: 'linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(168,85,247,0.18) 100%)',
          border: '1.5px solid rgba(168,85,247,0.5)',
          boxShadow: '0 0 22px rgba(168,85,247,0.4), inset 0 1px 0 rgba(255,255,255,0.06)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center"
            style={{
              background: 'linear-gradient(140deg, rgba(168,85,247,0.5), rgba(168,85,247,0.2))',
              boxShadow: '0 0 12px rgba(168,85,247,0.6), inset 0 0 0 1px rgba(168,85,247,0.55)',
            }}
          >
            <Zap
              className="w-5 h-5"
              style={{ color: '#ddd6fe', filter: 'drop-shadow(0 0 6px rgba(168,85,247,0.9))' }}
            />
          </div>
          <div>
            <h2
              className="font-heading text-base font-black uppercase tracking-tight"
              style={{ color: '#ddd6fe', textShadow: '0 0 5px rgba(168,85,247,0.55)' }}
            >
              {label('Essentiel', 'Essentials', 'Esencial')}
            </h2>
            <p className="text-xs text-white/60">
              {label(
                '5 champs pour que le Coach YUMIX puisse générer ton plan.',
                '5 fields so Coach YUMIX can generate your plan.',
                '5 campos para que Coach YUMIX genere tu plan.',
              )}
            </p>
          </div>
        </div>

        {/* Unit toggle */}
        <div className="flex gap-2 mb-4">
          {Object.entries(UNIT_SYSTEMS).map(([key, sys]) => (
            <PillOption
              key={key}
              active={unitSystem === key}
              onClick={() => setUnitSystem(key)}
              testId={`bypass-unit-${key}`}
            >
              {sys.label?.[language] || key}
            </PillOption>
          ))}
        </div>

        {/* Weight */}
        <div className="mb-3">
          <Label htmlFor="bypass-weight" className="text-xs text-white/70 uppercase tracking-wider mb-1 block">
            {label('Poids actuel', 'Current weight', 'Peso actual')} ({isImperial ? 'lbs' : 'kg'})
          </Label>
          <Input
            id="bypass-weight"
            type="number"
            step="0.1"
            value={bypassData.weight}
            onChange={(e) => update({ weight: e.target.value })}
            placeholder={isImperial ? '175' : '80'}
            data-testid="bypass-weight-input"
            className="bg-black/40 border-violet-500/30 text-white placeholder:text-white/30"
          />
        </div>

        {/* Target weight */}
        <div className="mb-3">
          <Label htmlFor="bypass-target-weight" className="text-xs text-white/70 uppercase tracking-wider mb-1 block">
            {label('Poids cible', 'Target weight', 'Peso objetivo')} ({isImperial ? 'lbs' : 'kg'})
          </Label>
          <Input
            id="bypass-target-weight"
            type="number"
            step="0.1"
            value={bypassData.target_weight}
            onChange={(e) => update({ target_weight: e.target.value })}
            placeholder={isImperial ? '175' : '80'}
            data-testid="bypass-target-weight-input"
            className="bg-black/40 border-violet-500/30 text-white placeholder:text-white/30"
          />
        </div>

        {/* Height */}
        {isImperial ? (
          <div className="mb-3 grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-white/70 uppercase tracking-wider mb-1 block">
                {label('Taille', 'Height', 'Estatura')} (ft)
              </Label>
              <Input
                type="number"
                value={bypassData.height_feet}
                onChange={(e) => update({ height_feet: e.target.value })}
                placeholder="5"
                data-testid="bypass-height-ft-input"
                className="bg-black/40 border-violet-500/30 text-white placeholder:text-white/30"
              />
            </div>
            <div>
              <Label className="text-xs text-white/70 uppercase tracking-wider mb-1 block">&nbsp;(in)</Label>
              <Input
                type="number"
                value={bypassData.height_inches}
                onChange={(e) => update({ height_inches: e.target.value })}
                placeholder="10"
                data-testid="bypass-height-in-input"
                className="bg-black/40 border-violet-500/30 text-white placeholder:text-white/30"
              />
            </div>
          </div>
        ) : (
          <div className="mb-3">
            <Label htmlFor="bypass-height" className="text-xs text-white/70 uppercase tracking-wider mb-1 block">
              {label('Taille', 'Height', 'Estatura')} (cm)
            </Label>
            <Input
              id="bypass-height"
              type="number"
              value={bypassData.height}
              onChange={(e) => update({ height: e.target.value })}
              placeholder="178"
              data-testid="bypass-height-input"
              className="bg-black/40 border-violet-500/30 text-white placeholder:text-white/30"
            />
          </div>
        )}

        {/* Age */}
        <div className="mb-3">
          <Label htmlFor="bypass-age" className="text-xs text-white/70 uppercase tracking-wider mb-1 block">
            {label('Âge', 'Age', 'Edad')}
          </Label>
          <Input
            id="bypass-age"
            type="number"
            value={bypassData.age}
            onChange={(e) => update({ age: e.target.value })}
            placeholder="35"
            data-testid="bypass-age-input"
            className="bg-black/40 border-violet-500/30 text-white placeholder:text-white/30"
          />
        </div>

        {/* Sex */}
        <div className="mb-3">
          <Label className="text-xs text-white/70 uppercase tracking-wider mb-1 block">
            {label('Sexe biologique', 'Biological sex', 'Sexo biológico')}
          </Label>
          <div className="grid grid-cols-3 gap-2">
            <PillOption active={bypassData.sex === 'male'} onClick={() => update({ sex: 'male' })} testId="bypass-sex-male">
              {label('Homme', 'Male', 'Hombre')}
            </PillOption>
            <PillOption active={bypassData.sex === 'female'} onClick={() => update({ sex: 'female' })} testId="bypass-sex-female">
              {label('Femme', 'Female', 'Mujer')}
            </PillOption>
            <PillOption active={bypassData.sex === 'other'} onClick={() => update({ sex: 'other' })} testId="bypass-sex-other">
              {label('Autre', 'Other', 'Otro')}
            </PillOption>
          </div>
        </div>

        {/* Goal */}
        <div className="mb-5">
          <Label className="text-xs text-white/70 uppercase tracking-wider mb-1 block">
            {label('Objectif', 'Goal', 'Objetivo')}
          </Label>
          <div className="grid grid-cols-3 gap-2">
            <PillOption active={bypassData.goal === 'lose'} onClick={() => update({ goal: 'lose' })} testId="bypass-goal-lose">
              {label('Perdre', 'Lose', 'Perder')}
            </PillOption>
            <PillOption active={bypassData.goal === 'maintain'} onClick={() => update({ goal: 'maintain' })} testId="bypass-goal-maintain">
              {label('Maintenir', 'Maintain', 'Mantener')}
            </PillOption>
            <PillOption active={bypassData.goal === 'gain'} onClick={() => update({ goal: 'gain' })} testId="bypass-goal-gain">
              {label('Prendre', 'Gain', 'Ganar')}
            </PillOption>
          </div>
        </div>

        <button
          type="button"
          onClick={onSubmit}
          disabled={loading}
          data-testid="bypass-submit-btn"
          className="w-full py-3 rounded-full font-black uppercase tracking-widest text-xs transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          style={{
            background: 'linear-gradient(140deg, #a855f7 0%, #7c3aed 55%, #6d28d9 100%)',
            color: '#fff',
            boxShadow: '0 0 22px rgba(168,85,247,0.65), inset 0 1px 0 rgba(255,255,255,0.2)',
            textShadow: '0 0 5px rgba(0,0,0,0.4)',
          }}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {label('Chargement…', 'Loading…', 'Cargando…')}
            </>
          ) : (
            <>
              <Zap className="w-4 h-4" />
              {label('Aller au Coach YUMIX', 'Go to Coach YUMIX', 'Ir al Coach YUMIX')}
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
}
